package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.UserRepository
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.util.AppClock.now

class AddEngagementUseCase(
    private val userRepository: UserRepository
) {
    /**
     * Nadaje użytkownikowi dostęp do miejsca pracy (tworzy wpis w users/{uid}/engagements).
     * UWAGA: To ustawia tylko relację operacyjną.
     * Dane finansowe (stawki) muszą być ustawione osobno przez `SetUserFinancialsUseCase` (w sensitive data).
     *
     * SH-192: with [financial] (an existing person taken into another workplace of the business),
     * the workplace's pay terms are written in the same batch; the person's other engagements and
     * contact details stay as they are.
     */
    suspend operator fun invoke(
        userId: String,
        workplaceId: String,
        workplaceName: String,
        roles: String?,
        skills: List<String>,
        permission: UserPermission,
        isPrimary: Boolean,
        competence: Competence = Competence.INDEPENDENT,
        financial: FinancialEngagement? = null,
    ): Result<Unit> {
        return try {
            // 1. Generujemy dzisiejszą datę startu (YYYY-MM-DD)
            val today = now()
                .toLocalDateTime(TimeZone.currentSystemDefault())
                .date
                .toString()

            // 2. Tworzymy obiekt Engagement zgodny z Twoim modelem
            val engagement = Engagement(
                workplaceId = workplaceId,
                cachedWorkplaceName = workplaceName,
                role = roles,
                skills = skills,
                permission = permission,
                isActive = true,
                startDate = today, // "2026-01-30"
                endDate = null,
                isPrimary = isPrimary,
                competence = competence,
            )

            // 3. Zapisujemy w publicznym profilu użytkownika
            if (financial != null) {
                userRepository.addEngagementWithPayTerms(userId, engagement, financial).getOrThrow()
            } else {
                userRepository.addEngagement(userId, engagement).getOrThrow()
            }

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}